import type { BundleView, DataFlag, Intervention, MandateView, Receipt, ScheduleBlock, SignedStep } from "../api/types.js";
import { doneOf, endLine, scheduledEnd, type Done, type EndKind } from "../app/card-end.js";
import { CHECK_PAYEE, composeScamNotice, SCAM_QUESTION_IDS, type ScamAnswer, type ScamNotice, type ScamQuestionId } from "../app/scam.js";
import { el } from "../lib/dom.js";
import { chainOf } from "../app/chain.js";
import { describeStep, leaves, protectiveOnly, shownAs, signedHold, signedLeaves, surfaceLead, surfaceLeadInert, type Inert, type Row } from "../app/describe.js";
import { UNLOCK_SIGN } from "../app/security-model.js";
import { inertNotices } from "../app/inert.js";
import { addWaitLine, heldLine, isCooling, KYC_DUE_REST, kycDueDate, tokenLine } from "../app/notices.js";
import { bundleTotal, dateTime, signedTotal } from "../lib/format.js";
import { icon, type IconId } from "../lib/icon.js";
import { arrowTo, keepParts } from "../lib/keep.js";
import { fitHeader, watchHeader } from "./bank-header.js";
import { inertNotice, refs } from "./inert.js";
import { ruleId } from "./rule-id.js";
import { reducedMotion } from "../lib/root.js";
import { boundLine, controlNames, sourceName } from "../app/edit-model.js";
import { detailTail, footerLine, replaceLine, scheduleLead, signLabel, tokenLead, upTo } from "../app/schedule-words.js";
import { scheduleBlock } from "./schedule-block.js";

/**
 * The bank card — the legal moment of the product (UX spec v3 §6.2, mock-up
 * 02-chat.html). "This card is the bank, not the assistant": the white Bank
 * tag, the bank's button, no apricot, no gradient.
 *
 * It is drawn from ONE thing: the bundle the gateway returned for
 * `GET /v1/bundles/{id}` — the signing payload plus the interventions beside it.
 * Never from chat state, never from a draft. Names are printed as signed
 * (`nickname`, `registered_name`, the registry's masked proxy); amounts are the
 * payload's decimal strings; the only arithmetic is the header total and the
 * money leaving each account, both sums of the payload's own amounts in integer
 * minor units. Every string from data goes in with `textContent`.
 *
 * States: to-sign · busy (the passkey prompt is up) · editing · superseded ·
 * revised, plus a failure line (cancelled / timed out) and the composed scam
 * notice at the gateway's level; and the four ends of §C.10 — signed · expired ·
 * cancelled · not sent — where the sign button is removed from the card (not
 * greyed, not disabled: gone) and one end line takes its place.
 *
 * DCTA-124: the same card draws a payload one of the bank's own screens made (`origin`, §F.2 row 13 —
 * Check and sign for an add): its header is the lead that says what is signed, its row is not money,
 * its primary is "Sign · add payee" and "Not now" stands in Edit's place. One card, one way to sign.
 */
export interface BankCardHandlers {
  onSign(): void;
  onEdit(): void;
  onReask(): void;
  /** Level question: a "Yes" to either question — which one (§F.2 21g: the paused end names what it is a sign of). */
  onScamYes?(question: ScamQuestionId): void;
  /** Any scam level: "Talk to us" under the button (§6.7) — the bank's number, on a sheet. */
  onTalk?(): void;
  /** Signed: the end line's Receipt (§C.10). */
  onReceipt?(): void;
  /** Not sent: the end line's Try again (§C.10). */
  onRetry?(): void;
  /** The balance moved (POL-BAL-PROJECTED): the end line's Update — the same payment, drawn again. */
  onUpdate?(): void;
  /** Judge mode is on: the end line's rule id is shown (DCTA-124: a rule id is never on the customer's screen). */
  judge?(): boolean;
  /** DCTA-209 (27k): a schedule card's "Not now" — Edit's place; the card ends as cancelled. */
  onNotNow?(): void;
  /** DCTA-209 (27m): a signed schedule's end line "View" — the schedule it created (route 2). */
  onView?(mandateId: string): void;
}

/** DCTA-209: what a schedule card reads beside its payload — the schedule a change replaces, as route 2 read it (27j). */
export interface BankCardOptions {
  readonly replaces?: MandateView;
}

type Life = "live" | "editing" | "superseded" | EndKind;

/**
 * DCTA-170 (F34; docs/design/v3/03-edit-in-place.md §0.2, mock-up 21-edit.html) — one row of the card's edit state, as the
 * editor (`app/editor.ts`) hands it over. A step keeps its title, sub-line and figure, drawn from the payload; `offer` is
 * what the row can do ("amount": the remove disc and the figure as a control; "follows": the bank's funding move, neither;
 * "none": the disc only). A removed step is one line and Undo until Done (22s).
 */
export type EditRow =
  | {
      readonly kind: "step";
      readonly step: SignedStep;
      readonly offer: "amount" | "follows" | "none";
      /** Row 11's {what} — the controls' names (22c, 22d). */
      readonly what: string;
      /** "follows": "Changes with the order for {share}". */
      readonly follows?: string;
      /** In flight ≥ 150 ms: this figure will change (the skeleton holds its place). */
      readonly pending?: boolean;
      /** F39: its money could come from another of the customer's accounts — "from {account}" is the account control. */
      readonly source?: boolean;
    }
  | { readonly kind: "removed"; readonly stepId: string; readonly what: string };

export interface EditView {
  readonly rows: readonly EditRow[];
  /** The hint line: "Tap an amount to change it." · "Updating" (in flight ≥ 150 ms) · hidden (the last step's question is open). */
  readonly hint: "idle" | "busy" | "hidden";
  /** Row 22x: the edit did not reach the bank (with Try again), or the card changed under it (no retry). */
  readonly error?: LineError;
  /** A change was made: "Keep it as it was" beside Done (22f). */
  readonly changed: boolean;
  /** Removing the only step left (22w): asked in the card; the row's controls rest while it is open. */
  readonly last?: { readonly what: string };
  /** The header total will change (in flight ≥ 150 ms). */
  readonly totalPending?: boolean;
}

export interface EditHandlers {
  onRemove(stepId: string): void;
  onAmount(stepId: string): void;
  /** F39: the account control — the account sheet. */
  onSource(stepId: string): void;
  onUndo(stepId: string): void;
  onDone(): void;
  onKeepAsWas(): void;
  onCancelLast(): void;
  onKeepLast(): void;
}
/** The card's error line (row 22x): the words, and Try again when the edit never reached the bank. */
export interface LineError {
  readonly text: string;
  readonly onRetry?: () => void;
}
/**
 * The row's change line (22u, the bank's signed words; the pencil unless it is the bank's own move) and, under it, ADR-0022's
 * quiet line for a figure above a limit the customer set earlier — one cell, the second line wrapping under the first.
 */
function changeLines(line: string | undefined, bound: string | undefined, pencil: boolean): HTMLElement[] {
  if (line === undefined && bound === undefined) return [];
  return [
    el("span", { class: "chg" }, [
      ...(line === undefined ? [] : [...(pencil ? [icon("i-pencil")] : []), line]),
      ...(bound === undefined ? [] : [el("span", { class: "bound" }, [bound])]),
    ]),
  ];
}

/** 22s: "Removed: {what}" and its Undo ("Undo removing {what}") — on the removed row in the edit state, and above Sign after Done. */
function removedParts(stepId: string, what: string, onUndo: (stepId: string) => void): HTMLElement[] {
  const undo = el("button", { class: "raw undo", type: "button", data: { step: stepId }, aria: { label: controlNames(what).undo } }, [icon("i-undo"), "Undo"]);
  undo.addEventListener("click", () => onUndo(stepId));
  return [el("span", { class: "rt" }, [`Removed: ${what}`]), undo];
}
export type SignFailure = "cancelled" | "timed-out" | "create-cancelled";
const ENDS: ReadonlySet<Life> = new Set<Life>(["signed", "expired", "cancelled", "not-sent", "changed"]);

/** A line on a notice: a bold lead, the rest in the notice's ink, and a rule id on refusals only. */
export interface EndNotice {
  readonly icon: IconId;
  readonly lead: string;
  readonly rest?: string;
  readonly cls?: string;
}

export interface EndAction {
  readonly label: string;
  readonly kind: "btn" | "ghost" | "link";
  readonly onSelect: () => void;
  /** A link out of the product (a phone number): an anchor, not a button. */
  readonly href?: string;
}

/** The card's primary slot after the signature (05: "Confirm · S$6,000.00"; a schedule: "Confirm · S$200.00 a month"). */
export interface EndPrimary {
  readonly verb: string;
  /** The figure, set as money; "" for none (a card bill's rule: "Confirm · every month"). */
  readonly money: string;
  /** DCTA-209: words after the figure (" a month"), or in its place ("every month"). */
  readonly rest?: string;
  readonly icon: IconId;
  readonly onSelect: () => void;
}

/**
 * What the bottom of the card becomes after the signature or at a stop — 05's
 * waiting / cancel / Not sent, 06's held / cancelled, 07's paused. The rows and
 * the header stay exactly as signed; only the status, the notices and the
 * actions change. Everything is set by a controller from the payload, the
 * receipt and the copy deck.
 */
export interface CardEnd {
  readonly status: string;
  readonly pos?: boolean;
  readonly label: string;
  readonly leaves: boolean;
  readonly notices: readonly EndNotice[];
  readonly extra?: HTMLElement;
  readonly primary?: EndPrimary;
  readonly acts: readonly EndAction[];
  /**
   * DCTA-155 §F.2 21h (F24): where focus goes as this end appears — "lead": its first notice (`tabindex=-1`), so a
   * screen reader reads the paused end before its ways on. Without it, focus follows the controls (below).
   */
  readonly focus?: "lead";
}

const FAILURE: Readonly<Record<SignFailure, readonly [string, string]>> = {
  cancelled: ["Nothing was signed.", " Sign when you're ready."],
  "timed-out": ["That took too long, so nothing was signed.", " Try again."],
  "create-cancelled": ["No passkey was saved, so nothing was signed.", " Sign again when you're ready."],
};

/** A retired card's line (D1, 11 Oct; P1-3, DCTA-192): replaced only when the bank withdrew it for the card below. */
const RETIRED_LINE = { replaced: "Replaced by the card below.", retired: "You asked for something else next." } as const;

/** §F.2 13g: an add's passkey sheet closed — what did not happen, said in the add's terms. */
const ADD_CANCELLED: readonly [string, string] = ["Nothing was signed, so no payee was added.", " Sign when you're ready."];

/** F38: how far a swipe opens a row (the "Remove" action's width), how far it must travel to stay open, and the edge it never starts at. */
const SWIPE_OPEN = 96;
const SWIPE_COMMIT = 40;
const SWIPE_EDGE = 20;

/** DCTA-209 (27j): the replace notice when route 2's read of the old schedule did not come back (copy for ux-designer). */
const REPLACES_ONE = { lead: "Replaces one of your scheduled payments.", rest: " That one stops when you sign this." } as const;

/** DCTA-209 (decision 8): a row without the bank's derivation fragment (a card bill's rule: no day-of-signing figure). */
function withoutDerivation(r: Row): Row {
  const { derivation: _left, ...rest } = r;
  return rest;
}

/** Ids for a linked row's description (its derivation): unique on the page, however many cards it holds. */
let derivations = 0;
/** Ids for a scam question, the name of its group (§F.2 21d): unique on the page, however many cards it holds. */
let scamQuestions = 0;

export class BankCard {
  readonly element: HTMLElement;
  readonly bundle: BundleView;
  readonly #handlers: BankCardHandlers;
  readonly #count: string;
  readonly #total: string | undefined;
  /** DCTA-124: the lead of a payload a bank screen made ("Add … as a payee"); undefined for the conversation's. */
  readonly #lead: string | undefined;
  /** Every step adds a payee: no money on this card. */
  readonly #adds: boolean;
  readonly #status: HTMLElement;
  /** P0-5 (DCTA-192): the header, fitted whenever its pill changes (ui/bank-header.ts). */
  readonly #bh: HTMLElement;
  readonly #bf: HTMLElement;
  readonly #sign: HTMLButtonElement;
  readonly #signLabel: HTMLElement;
  readonly #why: HTMLElement;
  readonly #endline: HTMLElement;
  readonly #endHead: HTMLElement;
  readonly #endRest: HTMLElement;
  readonly #endText: HTMLElement;
  /** A refusal's rule id on the end line — in the page only when the end has one (DCTA-101 run 2). */
  #endRule: HTMLElement | undefined;
  readonly #endGo: HTMLButtonElement;
  readonly #edit: HTMLButtonElement;
  readonly #show: HTMLButtonElement;
  /**
   * D1 (the coordinator's ruling, 11 Oct): a card the bank withdrew with a combined card says it was replaced by the card
   * below; P1-3 (DCTA-192): one retired by an unrelated request says the customer asked for something else next.
   */
  readonly #repl: HTMLElement;
  readonly #talk: HTMLButtonElement;
  /** A scam signal is on the card and there is somewhere to talk: Talk to us takes Edit's place (the mock-up's two actions). */
  readonly #scam: boolean;
  readonly #leaves: HTMLElement;
  readonly #notices: HTMLElement[] = [];
  /**
   * DCTA-143: every flagged string the card prints (its rows' and its lead's inert forms, describe.ts `Inert`) — any line
   * the card composes around one (a held line, a phase's notice, the money line) sets it apart as data with `refs`.
   */
  readonly #inert: readonly Inert[];
  #failure: HTMLElement | undefined;
  readonly #end: HTMLElement;
  #endSpec: CardEnd | undefined;
  #endPrimary: HTMLButtonElement | undefined;
  #endWhy: HTMLElement | undefined;
  #life: Life = "live";
  /** The bank's time of the end (signed_at, expires_at, a cancel's time) and what ran. */
  #endAt: string | undefined;
  #done: Done | undefined;
  /** §C.11: the line above the button — the first payment's promise, then "Passkey saved." */
  #line: string | undefined;
  #revised = false;
  #retired = false;
  /** The end line's one action was used and a newer card carries it on (Update). */
  #spent = false;
  #busy = false;
  #scamBlocked = false;
  /** §F.2 21e: the last No just woke Sign — the polite line says "You can sign now." in this one paint. */
  #signNow = false;
  /** Question-level answers by `${step_id}:${question_id}` — a bundle may ask about two steps. */
  readonly #answers = new Map<string, ScamAnswer>();
  /** DCTA-170: the rows as signed, and the edit state's own parts (F34). */
  readonly #rows: HTMLElement;
  readonly #erows: HTMLElement;
  readonly #ehint: HTMLElement;
  readonly #eerr: HTMLElement;
  readonly #efoot: HTMLElement;
  readonly #elast: HTMLElement;
  readonly #rmlines: HTMLElement;
  /** After Done (22s, §2.8): an Undo that failed, said in the card's error line outside the edit state. */
  #lineErr: LineError | undefined;
  /** One polite status in the card (§0.2 Accessibility): "Updating", "Changed to S$800.00.", "Removed …". */
  readonly #say: HTMLElement;
  readonly #totalEl: HTMLElement | undefined;
  #editView: EditView | undefined;
  #editH: EditHandlers | undefined;
  /**
   * DCTA-209 (F55): the payload's signed schedule — a mandate card. Its header is the lead, its block sits under the row,
   * its footer says nothing leaves today, its sign label names the rule, and "Not now" stands in Edit's place.
   */
  readonly #schedule: ScheduleBlock | undefined;
  /** The footer's schedule parts (the replace notice, "Nothing leaves today."): live only, like the notices. */
  readonly #schedFoot: HTMLElement[] = [];
  /** DCTA-209 (F56): the schedule the signature created — the receipt's `mandate` — and its first payment's date. */
  #mandate: { readonly id: string; readonly first: string | undefined } | undefined;
  /** DCTA-209: the schedule block — folded to its rule while a phase after the signature asks for more (the token). */
  readonly #schedEl: HTMLElement | undefined;

  constructor(bundle: BundleView, handlers: BankCardHandlers, opts: BankCardOptions = {}) {
    this.bundle = bundle;
    this.#handlers = handlers;
    const steps = bundle.signing_payload.steps;
    const schedule = bundle.signing_payload.schedule;
    this.#schedEl = schedule === undefined ? undefined : scheduleBlock(schedule, bundle.schedule_note);
    this.#schedule = schedule;
    this.#count = steps.length === 1 ? "1 operation" : `${String(steps.length)} operations`;
    // A /2 payload signs its per-account totals (contract §4.4): the header reads those; a /1
    // payload (verifies until retired) is summed from its own steps as before. DCTA-131: a chain (one
    // step follows from another) is numbered and linked; when a funding move's money passes through the
    // trading account, no sum is printed at all — it would count the same money twice.
    const summary = bundle.signing_payload.bundle;
    const chain = chainOf(steps);
    // DCTA-136 (F10): the bank's flags on the payload's display strings, beside it (never signed, never a rewrite).
    const flags = bundle.firewall?.data_flags ?? [];
    // DCTA-209 (27l): a schedule card's row says when each payment goes, never "arrives now".
    const tail = schedule === undefined ? undefined : detailTail(schedule);
    const described = steps.map((step) => describeStep(step, flags, tail));
    // DCTA-209: a schedule card has no header total (its row and its sign label carry the figure; nothing moves at signing).
    // DCTA-232: nor has a protective card (the bank's unlock card): no money moves, it never reads as a payment.
    this.#total = schedule !== undefined || chain.passThrough || protectiveOnly(steps) ? undefined : summary === undefined ? bundleTotal(steps) : signedTotal(summary.totals);
    this.#lead = surfaceLead(bundle.signing_payload, flags);
    const leadName = surfaceLeadInert(bundle.signing_payload, flags);
    this.#inert = [...described.flatMap((r) => r.inert ?? []), ...(leadName === undefined ? [] : [leadName])];
    this.#adds = steps.length > 0 && steps.every((s) => s.type === "payee_add");

    this.#status = el("span", { class: "status", hidden: true });
    const header = el("div", { class: "bh" }, [
      el("span", { class: "banktag" }, ["Bank"]),
      // §F.2 13b: a bank screen's card says what is signed; the conversation's counts it and sums it. DCTA-209 (27a): a
      // schedule card says what it is ("Repeat payment", "Scheduled payment").
      ...(schedule !== undefined
        ? [el("b", { class: "lead" }, [scheduleLead(schedule)])]
        : this.#lead !== undefined
        ? [el("b", { class: "lead" }, refs(this.#lead, this.#inert))]
        : // P0-5 (DCTA-192): one operation draws no count (its row says what it is; the card's name still counts it).
          [...(steps.length === 1 ? [] : [el("b", { class: "count" }, [this.#count])]), ...(this.#total === undefined ? [] : [el("span", { class: "money total" }, [this.#total])])]),
      this.#status,
    ]);
    this.#totalEl = header.querySelector<HTMLElement>(".total") ?? undefined;
    this.#bh = header;
    watchHeader(header);

    const cap = schedule === undefined ? undefined : upTo(schedule);
    const rows = el(
      "div",
      { class: chain.numbered ? "rows chain" : "rows" },
      steps.map((step, i) => {
        const described0 = described[i] ?? describeStep(step, flags);
        // DCTA-209 (decision 8): a card bill's rule never shows the day-of-signing bill figure — not as the amount, and not
        // in the derivation the bank may sign beside it — it would read as the amount authorised.
        const r: Row = cap === undefined ? described0 : withoutDerivation(described0);
        if (step.type === "payee_add") {
          // §F.2 13c: the proxy and the customer's name for them — no money, nothing to expand.
          return el("div", { class: "op" }, [
            el("span", { class: "ic" }, [icon(r.ic)]),
            el("span", { class: "tx" }, [el("b", {}, refs(r.title, r.inert)), ...(r.sub === "" ? [] : [el("small", {}, refs(r.sub, r.inert))])]),
          ]);
        }
        // DCTA-219: held — a hold signed beside a token step too: its release chip, as the held notice says.
        const until = signedHold(step);
        const held = until !== undefined;
        // DCTA-131: on a chain the tile carries the step's number ("Step 1" to a screen reader, as the bank's own
        // "half of step 1" reads); the rail joins a row to the one above it that it follows from.
        const link = chain.rows[i];
        const n = link?.n;
        const linked = link?.linked === true;
        const links = chain.rows[i + 1]?.linked === true;
        // DCTA-170 (B48, §F.2 22u): a figure an edit changed says so on the row itself, in the bank's signed words.
        const changedLine = step.amended?.line;
        const bound = boundLine(step);
        const cls = ["raw", "op", ...(held ? ["held"] : []), ...(links ? ["links"] : []), ...(linked ? ["linked"] : []), ...(changedLine === undefined && bound === undefined ? [] : ["changed"])].join(" ");
        const shownClosed = r.derivation !== undefined && step.amount_derivation?.kind === "shortfall_for_steps";
        // A linked row's derivation says what the rail draws; it is the row's description, so it is heard too.
        const drvId = r.derivation !== undefined && linked && !shownClosed ? `drv-${String(++derivations)}` : undefined;
        const row = el("button", { class: cls, type: "button", aria: { expanded: "false", ...(drvId === undefined ? {} : { describedby: drvId }) } }, [
          n === undefined
            ? el("span", { class: "ic" }, [icon(r.ic)])
            : el("span", { class: "ic n" }, [el("span", { class: "vis-hidden" }, ["Step "]), n]),
          el("span", { class: "tx" }, [
            el("b", {}, refs(r.title, r.inert)),
            // DCTA-160b: a funding move the bank sized itself (PQ-16, `shortfall_for_steps`) says its arithmetic under the
            // sub-line, closed — the customer never said that figure. Every other derivation keeps §F.2 row 15's place.
            el("small", {}, [...refs(r.sub, r.inert), ...(shownClosed ? [el("span", { class: "drv-line" }, [r.derivation as string])] : [])]),
          ]),
          // DCTA-209 (27g): a card bill's rule prints its signed cap, "up to" — never the day-of-signing bill figure.
          el("span", { class: "amt money" }, cap === undefined ? [r.amount] : [el("span", { class: "upto" }, ["up to"]), ` ${cap}`]),
          ...(until === undefined ? [] : [el("span", { class: "st" }, [dateTime(until)])]),
          ...changeLines(changedLine, bound, true),
          // A computed amount's derivation is the bank's fragment, first in the opened row, joined with " · " (§F.2 row 15).
          // DCTA-136 (F10): a flagged reference closes the opened row, whole and quoted, after the bank's own words.
          el("span", { class: "dt" }, [
            ...(r.derivation === undefined || shownClosed ? [] : [el("span", { class: "drv", ...(drvId === undefined ? {} : { id: drvId }) }, [r.derivation]), " · "]),
            ...refs(r.detail, r.inert),
          ]),
        ]);
        row.addEventListener("click", () =>
          row.setAttribute("aria-expanded", row.getAttribute("aria-expanded") === "true" ? "false" : "true"),
        );
        return row;
      }),
    );

    // DCTA-209 (27h, decision 8): a schedule card has no money line at all — nothing leaves at signing, and its bill rule's
    // day-of-signing figure (the signed step's `amount`, in `bundle.totals`) is never in the card, hidden or not.
    this.#leaves =
      schedule !== undefined
        ? el("div", { class: "leaves", hidden: true })
        : summary === undefined
        ? el(
            "div",
            { class: "leaves" },
            // DCTA-143: a flagged account name is printed in its inert form here too, set apart as data.
            leaves(steps, flags).map((l) => el("span", {}, [...refs(l.name, this.#inert), " ", el("span", { class: "money" }, [l.amount])])),
          )
        : // /2: each account's signed total and the executor's projection — "Available before → after".
          el(
            "div",
            { class: "leaves signed" },
            signedLeaves(summary, steps, flags).map((l) =>
              el("div", { class: "lv" }, [
                el("span", { class: "nm" }, refs(l.name, this.#inert)),
                el("span", { class: "amt money" }, [l.amount]),
                ...(l.before === undefined || l.after === undefined
                  ? []
                  : [
                      el("span", { class: "ba" }, [
                        // 16-pay.html's delta pattern: the arrow is drawn, "to" is read.
                        "Available ",
                        el("span", { class: "money" }, [l.before]),
                        " ",
                        // P1-6 (DCTA-192): "→ after" is one piece — the line may break before the arrow, never after it.
                        arrowTo(l.after, "after money"),
                      ]),
                    ]),
              ]),
            ),
          );

    // DCTA-136 (F10, CC-121): a string the bank flagged is printed in its inert form, and the bank's sentence beside it
    // says so — first, right under the money, before any other notice; each distinct sentence once. Only for a string
    // the card prints: a flag on one it never shows would point at nothing. DCTA-143 (Q13: shown = signed): under the
    // sentence, "Show the full text" prints each of those strings whole, as signed (ui/inert.ts).
    for (const n of inertNotices(steps, this.#inert)) this.#notices.push(inertNotice(n.sentence, n.fulls));
    // DCTA-120: a step held for a new payee's 12-hour wait already says "X is a new payee" on its held
    // notice (§6.6) — the bank's new-payee signal is not said a second time over it (unless the level
    // is hold, where the scam notice carries the release line instead of the held notice).
    // DCTA-219: a hold signed beside a token step (`step_up_required` with `hold_until`) is a held notice too.
    const cooling = new Set(steps.filter((s) => isCooling(s) && signedHold(s) !== undefined).map((s) => s.step_id));
    for (const intervention of bundle.interventions ?? []) {
      const said =
        cooling.has(intervention.step_id) && intervention.level !== "hold"
          ? { ...intervention, signals: intervention.signals.filter((k) => k !== "POL-SCAM-NEW-PAYEE") }
          : intervention;
      const notice = this.#scamNotice(said, steps);
      if (notice !== undefined) this.#notices.push(notice);
    }
    // DCTA-120 (§F.2 6e): a PayNow number paid once ALWAYS asks the customer to check the name — on the
    // bank's scam notice when it raised the new-payee signal, else on a notice of its own. Read from the
    // signed payload (`payee.source`), never from the chat.
    steps.forEach((step, i) => {
      if (step.type !== "funds_transfer" || step.payee?.source !== "paynow_lookup") return;
      const said = (bundle.interventions ?? []).some((x) => x.step_id === step.step_id && x.signals.includes("POL-SCAM-NEW-PAYEE"));
      if (said) return;
      // DCTA-136 (F10): the name as the row prints it — its inert form when the bank flagged it.
      const r = described[i];
      const name = r?.name ?? step.payee.registered_name;
      this.#notices.push(
        el("div", { class: "notice scam" }, [icon("i-shield"), el("div", {}, [el("b", {}, refs(`You haven't paid ${name} before.`, r?.inert)), ` ${CHECK_PAYEE}`])]),
      );
    });
    // 06: a held step says when it goes out, so the signature covers a known
    // wait — unless the scam notice's hold line already says it for that step.
    const holdSaid = new Set((bundle.interventions ?? []).filter((i) => i.level === "hold").map((i) => i.step_id));
    // DCTA-131: two steps held for the same reason, time and amount (a split buy while SGX is shut) say the same
    // sentence — it is said once; each row keeps its own date.
    const heldSaid = new Set<string>();
    for (const step of steps) {
      // DCTA-143: a flagged payee name is said in its inert form, set apart as data, as on the row.
      const line = holdSaid.has(step.step_id) ? undefined : heldLine(step, flags);
      if (line === undefined || heldSaid.has(line.lead + line.rest)) continue;
      heldSaid.add(line.lead + line.rest);
      // P1-6 (DCTA-192): the release date and time stay on one line.
      this.#notices.push(el("div", { class: "notice held" }, [icon("i-clock"), el("div", {}, [el("b", {}, keepParts(refs(line.lead, this.#inert))), ...keepParts(refs(line.rest, this.#inert))])]));
    }
    // §F.2 13d: an add's wait, as the bank signed it — a duration, never a clock time.
    for (const step of steps) {
      const line = addWaitLine(step);
      if (line !== undefined) this.#notices.push(el("div", { class: "notice wait" }, [icon("i-clock"), el("div", {}, [el("b", {}, [line.lead]), line.rest])]));
    }
    // 05 warn / 07: a token code will follow the signature — said now, so the
    // signature covers a known second step and nobody is surprised after it. DCTA-209 (27i): on a schedule its lead
    // names no figure and points at what the card shows ("This much in all" — the At most row; "Payments this size").
    const token = tokenLine(steps);
    if (token !== undefined) {
      const lead = schedule === undefined ? token.lead : tokenLead(schedule);
      this.#notices.push(el("div", { class: "notice token" }, [icon("i-token"), el("div", {}, [el("b", {}, [lead]), token.rest])]));
    }
    // DCTA-209 (27j, 27h): a change says what it replaces (route 2's read of `supersedes`), then the footer's first line in
    // the money line's place — nothing leaves when a schedule is signed.
    if (schedule !== undefined) {
      // The payload signs `supersedes`, so signing stops another schedule: said always — named from route 2's read of that
      // id, or, when the read did not come back, without a name or a figure (never a guess).
      if (schedule.supersedes !== undefined) {
        const old = opts.replaces;
        const r = old !== undefined && old.mandate_id === schedule.supersedes ? replaceLine(old) : REPLACES_ONE;
        this.#schedFoot.push(el("div", { class: "notice replace" }, [icon("i-swap"), el("div", {}, [el("b", {}, [r.lead]), r.rest])]));
      }
      const f = footerLine(schedule, bundle.signing_payload.created_at);
      this.#schedFoot.push(el("p", { class: "later" }, [el("b", {}, [f.lead]), ...(f.rest === undefined ? [] : [f.rest])]));
    }
    // DCTA-176 (§F.2 26b, F48): the identity check is due, not lapsed — one line from the signed date; Sign stays active.
    // About the customer, not the payment: never read aloud (spoken.md §3.8). The date never breaks across lines.
    const due = kycDueDate(steps);
    if (due !== undefined) {
      this.#notices.push(
        el("div", { class: "notice due" }, [icon("i-id"), el("div", {}, [el("b", {}, ["Your identity check is due by ", el("span", { class: "nw" }, [due]), "."]), KYC_DUE_REST])]),
      );
    }

    this.#signLabel = el("span", { class: "lbl" });
    this.#sign = el("button", { class: "raw btn sign", type: "button" }, [icon("i-key"), this.#signLabel]);
    // The tap handler calls straight through: the controller's first statement is
    // navigator.credentials.get() (iOS drops the user gesture across an await).
    this.#sign.addEventListener("click", () => {
      if (this.#signDisabled()) return;
      this.#handlers.onSign();
    });
    // The line above the button changes under the customer's hand ("Answer both to sign" goes,
    // "Passkey saved." comes): said politely, not only shown.
    this.#why = el("p", { class: "why", hidden: true, aria: { live: "polite" } }, ["Answer both to sign"]);
    // §C.10: the end line — icon · what happened and when · the consequence · one small action.
    this.#endHead = el("b");
    this.#endRest = el("span");
    this.#endGo = el("button", { class: "raw go", type: "button" }, [el("span", { class: "gl" }), icon("i-chev-r")]);
    this.#endGo.addEventListener("click", () => {
      // DCTA-209 (27m): a signed schedule's end opens the schedule it created; any other signature, its receipt.
      if (this.#life === "signed" && this.#mandate !== undefined) this.#handlers.onView?.(this.#mandate.id);
      else if (this.#life === "signed") this.#handlers.onReceipt?.();
      else if (this.#life === "expired") this.#handlers.onReask();
      else if (this.#life === "not-sent") this.#handlers.onRetry?.();
      else if (this.#life === "changed" && !this.#spent) this.#handlers.onUpdate?.();
    });
    this.#endText = el("div", { class: "et", role: "status" }, [this.#endHead, this.#endRest]);
    this.#endline = el("div", { class: "endline", hidden: true }, [icon("i-check"), this.#endText, this.#endGo]);
    // §F.2 13e: on a bank screen's card the second action is "Not now" — the screen decides what it does.
    // DCTA-209 (27k): a schedule card has no Edit — a change is a new card; "Not now" ends it.
    this.#edit = el("button", { class: "raw btn link edit", type: "button" }, [this.#lead === undefined && schedule === undefined ? "Edit" : "Not now"]);
    this.#edit.addEventListener("click", () => (this.#schedule !== undefined ? this.#handlers.onNotNow?.() : this.#handlers.onEdit()));
    this.#repl = el("p", { class: "repl", hidden: true }, [RETIRED_LINE.replaced]);
    this.#show = el("button", { class: "raw btn link show", type: "button", hidden: true, aria: { expanded: "false" } }, [
      "Show the previous version",
    ]);
    this.#show.addEventListener("click", () => {
      const open = this.element.dataset["open"] !== "true";
      this.element.dataset["open"] = open ? "true" : "false";
      this.#show.setAttribute("aria-expanded", open ? "true" : "false");
      this.#show.textContent = this.#retired ? (open ? "Hide it" : "Show it") : open ? "Hide the previous version" : "Show the previous version";
    });

    this.#scam = (bundle.interventions ?? []).length > 0 && handlers.onTalk !== undefined;
    this.#talk = el("button", { class: "raw btn link talk", type: "button", hidden: true }, ["Talk to us"]);
    this.#talk.addEventListener("click", () => this.#handlers.onTalk?.());

    this.#end = el("div", { class: "end", hidden: true });
    // DCTA-170 (F34, F36): the edit state's parts — the hint, the error line, its rows, its footer, the last step's
    // question; and after Done, each removal's line above Sign (22s).
    this.#rows = rows;
    // Not `.rows`: a superseded card opened shows its `.rows` (shared.css 8.x) — never the edit state's.
    this.#erows = el("div", { class: "erows", hidden: true });
    this.#ehint = el("p", { class: "ehint", hidden: true });
    this.#eerr = el("div", { class: "eerr", role: "alert", hidden: true });
    this.#efoot = el("div", { class: "acts efoot", hidden: true });
    this.#elast = el("div", { class: "elast", hidden: true });
    this.#rmlines = el("div", { class: "rmlines", hidden: true });
    this.#say = el("div", { class: "vis-hidden say", role: "status", aria: { live: "polite" } });
    this.#bf = el("div", { class: "bf" }, [
      this.#rmlines,
      this.#leaves,
      ...this.#schedFoot,
      ...this.#notices,
      this.#why,
      this.#sign,
      this.#endline,
      this.#edit,
      this.#talk,
      this.#repl,
      this.#show,
      this.#end,
      this.#elast,
      this.#efoot,
    ]);
    // The bundle id is an attribute for the e2e suite and the judge proof; it is never text on the card.
    // DCTA-160b: what the bank left out of this card, in its own words — a quiet line after the rows (display only).
    const skipped = (bundle.skipped_steps ?? []).map((s) => el("p", {}, [s.customer_message]));
    this.element = el("div", { class: "bank", role: "group", data: { bundle: bundle.bundle_id } }, [
      header,
      this.#ehint,
      this.#eerr,
      this.#erows,
      rows,
      ...(skipped.length === 0 ? [] : [el("div", { class: "skipped" }, skipped)]),
      // DCTA-209 (F55): the schedule block, between the rows and the footer (shared.css 13.1).
      ...(this.#schedEl === undefined ? [] : [this.#schedEl]),
      this.#bf,
      this.#say,
    ]);
    this.#paint();
  }

  get signable(): boolean {
    return this.#life === "live" && !this.#signDisabled();
  }

  get life(): Life {
    return this.#life;
  }

  busy(on: boolean): void {
    this.#busy = on;
    this.#paint();
  }

  editing(on: boolean): void {
    if (this.#life !== "live" && this.#life !== "editing") return;
    this.#life = on ? "editing" : "live";
    this.#paint();
  }

  /**
   * DCTA-170 (F34): the card's edit state in place — the rows with their controls, the hint line, the error line, Done (and
   * "Keep it as it was" after a change), or the last step's question. `null` leaves it. Sign is never on the card while
   * it is drawn (§2.6): the card is "Editing" until Done.
   */
  editView(view: EditView | null, handlers?: EditHandlers): void {
    if (view === null) {
      this.#editView = undefined;
      this.#editH = undefined;
      this.#erows.replaceChildren();
      this.#drawError(this.#lineErr);
      if (this.#life === "editing") this.#life = "live";
      this.#paint();
      return;
    }
    if (this.#life !== "live" && this.#life !== "editing") return;
    this.#lineErr = undefined;
    this.#editView = view;
    if (handlers !== undefined) this.#editH = handlers;
    this.#life = "editing";
    this.#drawEdit(view);
    this.#paint();
  }

  /** After Done (22s): each removal's line and its Undo above Sign, until the card is signed. */
  removedLines(lines: readonly { readonly stepId: string; readonly what: string }[], onUndo: (stepId: string) => void): void {
    this.#rmlines.replaceChildren(
      ...lines.map((l) => el("div", { class: "rmline" }, removedParts(l.stepId, l.what, onUndo))),
    );
    this.#paint();
  }

  /**
   * After Done (22s, §2.8): an Undo that failed, in the card's error line under the header — row 22x's words with Try again
   * when it never reached the bank; the bank's refusal, or the stale line, without. `null` clears it. The edit state draws
   * its own error line and clears this one.
   */
  lineError(error: LineError | null): void {
    if (this.#editView !== undefined) return;
    this.#lineErr = error ?? undefined;
    this.#drawError(this.#lineErr);
    this.#paint();
  }

  /** The card's one polite status (§0.2): said once, never shown. */
  announce(text: string): void {
    this.#say.textContent = "";
    this.#say.textContent = text;
  }

  /** §0.2's focus rules: Edit → the hint; a change → that row's figure; a removal → its Undo; Done → the header. */
  focus(target: { readonly kind: "hint" | "header" | "keep-last" | "error" } | { readonly kind: "amount" | "undo" | "remove" | "source"; readonly stepId: string }): void {
    let node: HTMLElement | null = null;
    if (target.kind === "hint") node = this.#ehint;
    else if (target.kind === "header") node = this.element.querySelector<HTMLElement>(".bh");
    else if (target.kind === "keep-last") node = this.#elast.querySelector<HTMLElement>(".keep");
    else if (target.kind === "error") node = this.#eerr.querySelector<HTMLElement>(".retry") ?? this.#eerr;
    else if ("stepId" in target) {
      const id = CSS.escape(target.stepId);
      const sel = { amount: `.amt.ed[data-step="${id}"]`, undo: `.undo[data-step="${id}"]`, remove: `.rm[data-step="${id}"]`, source: `.src[data-step="${id}"]` }[target.kind];
      node = this.element.querySelector<HTMLElement>(sel);
    }
    if (node === null) return;
    if (node.tabIndex < 0 && !(node instanceof HTMLButtonElement)) node.tabIndex = -1;
    node.focus({ preventScroll: false });
  }

  #drawEdit(view: EditView): void {
    const h = this.#editH;
    const flags = this.bundle.firewall?.data_flags ?? [];
    const resting = view.last !== undefined;
    // A redraw never takes the customer's place (§0.2): the row control that had focus — its kind on its step — gets it
    // back in the new rows (the accounts' read, "Updating" and a reorder all redraw them). An explicit move the editor
    // makes after a change (card.focus) comes after this and wins. DCTA-198: found by e2e edit.spec under load.
    const kept = this.#focusedRowControl();
    this.#erows.replaceChildren(...view.rows.map((row) => this.#editRow(row, resting, flags)));
    if (kept !== undefined) this.element.querySelector<HTMLElement>(kept)?.focus({ preventScroll: true });
    // The hint line (22b) — busy (22t) after 150 ms, with the pill unchanged so the header never reflows; hidden while
    // the last step's question is open.
    this.#ehint.classList.toggle("busy", view.hint === "busy");
    this.#ehint.replaceChildren(...(view.hint === "busy" ? [icon("i-spin"), "Updating"] : ["Tap an amount to change it."]));
    this.#ehint.hidden = view.hint === "hidden";
    this.element.dataset["busy"] = view.hint === "busy" ? "true" : "false";
    this.element.setAttribute("aria-busy", view.hint === "busy" ? "true" : "false");
    this.#totalEl?.classList.toggle("pend", view.totalPending === true);
    // Row 22x: the error line, the card as it was; Try again re-sends the same edit.
    this.#drawError(view.error);
    this.#eerr.hidden = view.error === undefined;
    // The footer: Done, and "Keep it as it was" after a change (22f); or the last step's question (22w).
    if (view.last !== undefined) {
      const cancel = el("button", { class: "raw btn", type: "button" }, ["Cancel it"]);
      const keep = el("button", { class: "raw btn ghost keep", type: "button" }, ["Keep it"]);
      cancel.addEventListener("click", () => h?.onCancelLast());
      keep.addEventListener("click", () => h?.onKeepLast());
      this.#elast.replaceChildren(
        el("div", { class: "notice cn" }, [icon("i-alert"), el("div", { class: "ntxt" }, [el("b", {}, [`Cancel ${view.last.what}?`]), " Nothing will be sent."])]),
        el("div", { class: "acts" }, [cancel, keep]),
      );
      this.#efoot.replaceChildren();
    } else {
      this.#elast.replaceChildren();
      const done = el("button", { class: "raw btn done", type: "button" }, ["Done"]);
      done.addEventListener("click", () => h?.onDone());
      const keepAsWas = el("button", { class: "raw btn link", type: "button" }, ["Keep it as it was"]);
      keepAsWas.addEventListener("click", () => h?.onKeepAsWas());
      this.#efoot.replaceChildren(done, ...(view.changed ? [keepAsWas] : []));
    }
  }

  /** The row control with focus, as a selector for its counterpart in the next drawing (`focus`'s), or none. */
  #focusedRowControl(): string | undefined {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.#erows.contains(active)) return undefined;
    const stepId = active.dataset["step"];
    const kind = ["rm", "src", "undo"].find((c) => active.classList.contains(c)) ?? (active.matches(".amt.ed") ? "amt.ed" : undefined);
    return stepId === undefined || kind === undefined ? undefined : `.${kind}[data-step="${CSS.escape(stepId)}"]`;
  }

  #drawError(error: LineError | undefined): void {
    if (error === undefined) {
      this.#eerr.replaceChildren();
      return;
    }
    const retry = el("button", { class: "raw retry", type: "button" }, ["Try again"]);
    if (error.onRetry !== undefined) retry.addEventListener("click", error.onRetry);
    this.#eerr.replaceChildren(icon("i-alert"), el("span", {}, [error.text]), ...(error.onRetry === undefined ? [] : [retry]));
  }

  #editRow(row: EditRow, resting: boolean, flags: readonly DataFlag[]): HTMLElement {
    const h = this.#editH;
    if (row.kind === "removed") return el("div", { class: "op removed", data: { step: row.stepId } }, removedParts(row.stepId, row.what, (id) => h?.onUndo(id)));
    const step = row.step;
    const r = describeStep(step, flags);
    const names = controlNames(row.what);
    const off = resting ? { disabled: "true" } : {};
    // The remove disc takes the icon's slot (a 44 px target, a 28 px disc, neutral ink — never red): not on a row the bank owns.
    const rm =
      row.offer === "follows"
        ? null
        : el("button", { class: "raw rm", type: "button", aria: { label: names.remove, ...off } }, [el("i", {}, [icon("i-minus")])]);
    if (rm !== null) {
      rm.dataset["step"] = step.step_id;
      rm.addEventListener("click", () => {
        if (rm.getAttribute("aria-disabled") !== "true") h?.onRemove(step.step_id);
      });
    }
    // The figure as a control (F34, Umakhan's change, 10 Oct: plain figure and pencil, no box — app.css 57), its name "Change {what}".
    let amount: HTMLElement;
    if (row.offer === "amount") {
      // In flight (22t): the figure itself shimmers in place — the skeleton is the figure's size, never the 44 px target's.
      const b = el("button", { class: "raw amt money ed", type: "button", aria: { label: names.change, ...off } }, [
        el("span", { class: `fig${row.pending === true ? " pend" : ""}` }, [r.amount]),
        icon("i-pencil"),
      ]);
      b.dataset["step"] = step.step_id;
      b.addEventListener("click", () => {
        if (b.getAttribute("aria-disabled") !== "true") h?.onAmount(step.step_id);
      });
      amount = b;
    } else {
      amount = el("span", { class: `amt money${row.pending === true ? " pend" : ""}` }, [r.amount]);
    }
    // 22u, once the bank changed the row (its move resized for the buy — H3): the signed line; until then the move says
    // what it follows.
    const line = step.amended?.line ?? (row.offer === "follows" ? row.follows : undefined);
    const bound = boundLine(step);
    const cls = ["op", "erow", ...(row.offer === "follows" ? ["follows"] : []), ...(line === undefined && bound === undefined ? [] : ["changed"])].join(" ");
    const node = el("div", { class: cls, data: { step: step.step_id } }, [
      el("span", { class: "ic" }, [icon(r.ic)]),
      ...(rm === null ? [] : [rm]),
      el("span", { class: "tx" }, [el("b", {}, refs(r.title, r.inert)), el("small", {}, row.source === true ? this.#fromControl(step, r, row.what, flags, off) : refs(r.sub, r.inert))]),
      amount,
      ...changeLines(line, bound, row.offer !== "follows"),
    ]);
    return rm === null || resting ? node : this.#swipeable(node, step.step_id);
  }

  /**
   * F39 (mock-up 21-edit `source`): the sub-line's "from {account}" as the account control — the words it shows are the
   * end of its name ("Change the account for the S$80.00 to Mum, from Everyday Savings"; WCAG 2.5.3, README R114).
   */
  #fromControl(step: SignedStep, r: Row, what: string, flags: readonly DataFlag[], off: { readonly disabled?: string }): (Node | string)[] {
    if (step.type !== "funds_transfer" && step.type !== "bill_payment") return refs(r.sub, r.inert);
    const from = shownAs(flags, step.step_id, "debit_account/display_name", step.debit_account.display_name);
    const tail = `from ${from}`;
    if (!r.sub.endsWith(tail)) return refs(r.sub, r.inert);
    const b = el("button", { class: "raw src", type: "button", aria: { label: sourceName(what, from), ...off } }, refs(tail, r.inert));
    b.dataset["step"] = step.step_id;
    b.addEventListener("click", () => {
      if (b.getAttribute("aria-disabled") !== "true") this.#editH?.onSource(step.step_id);
    });
    // Its own line (app.css 59): inline, under the figure's 44 px target, the two targets overlapped (README R116).
    return [...refs(r.sub.slice(0, -tail.length).replace(/\s*·\s*$/, ""), r.inert), b];
  }

  /**
   * F38 (§0.2, shared.css 11.3): a trailing swipe on a row in the edit state reveals "Remove" (96 px) — a tap on it is the
   * remove disc, which stays its accessible equivalent (the action is hidden from the accessibility tree and the tab order).
   * The drag starts inside the row and never at the screen's left edge (the system's back gesture); the row follows the
   * finger 1:1 and springs back short of 40 px; a full swipe never removes (Apple's allowsFullSwipe, off).
   */
  #swipeable(row: HTMLElement, stepId: string): HTMLElement {
    const action = el("button", { class: "raw swact", type: "button", tabIndex: -1, aria: { hidden: "true" } }, ["Remove"]);
    action.addEventListener("click", () => {
      wrap.classList.remove("open");
      this.#editH?.onRemove(stepId);
    });
    const wrap = el("div", { class: "swrap" }, [row, action]);
    let drag: { readonly id: number; readonly x: number; readonly y: number; readonly open: boolean; moved: boolean } | undefined;
    // The row follows the finger, so a drag that started on the figure ends on it: the click it makes is the drag's, not a tap.
    let swallow = false;
    row.addEventListener(
      "click",
      (e) => {
        if (!swallow) return;
        swallow = false;
        e.stopPropagation();
        e.preventDefault();
      },
      { capture: true },
    );
    const settle = (open: boolean): void => {
      row.style.transform = "";
      row.style.transition = "";
      wrap.classList.toggle("open", open);
      if (open) for (const other of this.#erows.querySelectorAll(".swrap.open")) if (other !== wrap) other.classList.remove("open");
    };
    row.addEventListener("pointerdown", (e) => {
      if (e.button !== 0 || e.clientX < SWIPE_EDGE) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, open: wrap.classList.contains("open"), moved: false };
    });
    row.addEventListener("pointermove", (e) => {
      if (drag === undefined || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x + (drag.open ? -SWIPE_OPEN : 0);
      const dy = e.clientY - drag.y;
      if (!drag.moved && Math.abs(e.clientX - drag.x) < 6) return;
      if (!drag.moved && Math.abs(dy) > Math.abs(e.clientX - drag.x)) {
        drag = undefined; // a vertical scroll, not a swipe
        return;
      }
      if (!drag.moved) {
        // A mouse that leaves the row mid-drag still ends the gesture here (touch is captured by itself).
        try {
          row.setPointerCapture(e.pointerId);
        } catch {
          /* the pointer is gone already: its pointercancel settles the row */
        }
      }
      drag.moved = true;
      row.style.transition = "none";
      row.style.transform = `translateX(${String(Math.max(-SWIPE_OPEN, Math.min(0, dx)))}px)`;
    });
    const end = (e: PointerEvent): void => {
      if (drag === undefined || e.pointerId !== drag.id) return;
      const moved = drag.moved;
      const travel = e.clientX - drag.x + (drag.open ? -SWIPE_OPEN : 0);
      const wasOpen = drag.open;
      drag = undefined;
      if (!moved && !wasOpen) return;
      // A drag — or a tap that closes an open row — is not also a tap on what is under the finger: the click this gesture
      // makes (dispatched right after the pointer goes up) is swallowed, and only that one.
      swallow = true;
      setTimeout(() => {
        swallow = false;
      }, 0);
      settle(moved && -travel >= SWIPE_COMMIT);
    };
    row.addEventListener("pointerup", end);
    row.addEventListener("pointercancel", () => {
      drag = undefined;
      settle(wrap.classList.contains("open"));
    });
    return wrap;
  }

  /**
   * Out of the stream's live slot for good. `revised`: Edit made a new version — "Superseded",
   * "Show the previous version". `retired`: an unrelated request came next — "Not sent",
   * "Show it" (DCTA-75 P2-9: "Superseded" is Edit's word for a revision only), and P1-3 (DCTA-192) "You asked for something
   * else next."; `replaced`: the bank withdrew it with a combined card (`cancelled_bundle_ids`, D1) — "Not sent", "Show it"
   * and "Replaced by the card below.".
   */
  supersede(why: "revised" | "retired" | "replaced" = "revised"): void {
    this.#retired = why !== "revised";
    this.#repl.textContent = why === "replaced" ? RETIRED_LINE.replaced : RETIRED_LINE.retired;
    this.#show.textContent = this.#retired ? "Show it" : "Show the previous version";
    this.#life = "superseded";
    this.element.dataset["open"] = "false";
    this.failure(null);
    this.#paint();
  }

  revised(): void {
    this.#revised = true;
    this.#paint();
  }

  /** The payload's own end of life: at its `expires_at` (§C.10). */
  expire(): void {
    if (!this.#endable()) return;
    this.#finish("expired", this.bundle.signing_payload.expires_at);
  }

  /**
   * Signed — with the bank's receipt when there is one (its `signed_at` and what ran;
   * called again with the final receipt after a step-up or a held step settles).
   */
  signed(receipt?: Receipt): void {
    if (this.#life !== "signed" && !this.#endable()) return;
    this.#done = receipt === undefined ? this.#done : doneOf(this.bundle.signing_payload.steps, receipt);
    // DCTA-209 (F56): the receipt's `mandate` — the schedule this signature created (nothing moved).
    const made = receipt?.steps.find((r) => r.mandate !== undefined)?.mandate;
    // The first payment's date is the one the customer signed (`first_on`), not a later read.
    if (this.#schedule !== undefined && made !== undefined) this.#mandate = { id: made.mandate_id, first: this.#schedule.first_on };
    this.#finish("signed", receipt?.signed_at ?? this.#endAt);
  }

  /** The bank cancelled this bundle; `at` is the bank's time of it, when it said. */
  cancelled(at?: string): void {
    if (!this.#endable()) return;
    this.#finish("cancelled", at);
  }

  /**
   * The bank ran nothing: the balance the payload signed moved before the signature arrived
   * (POLICY_STATE_CHANGED under POL-BAL-PROJECTED, DCTA-101 run 2). Nothing to sign any more;
   * the end line offers Update.
   */
  balanceChanged(): void {
    // From "signed" too: after a step-up the signature was verified and still nothing ran.
    if (this.#life !== "signed" && !this.#endable()) return;
    this.#finish("changed", undefined);
  }

  /** Update drew the newer card: the old card keeps its end, without the spent action. */
  updated(): void {
    this.#spent = true;
    this.#paint();
  }

  /** The bank could not be reached, and its own record says nothing moved (§C.10). */
  notSent(): void {
    if (!this.#endable()) return;
    this.#finish("not-sent", undefined);
  }

  #endable(): boolean {
    return this.#life === "live" || this.#life === "editing";
  }

  #finish(kind: EndKind, at: string | undefined): void {
    this.#life = kind;
    this.#endAt = at;
    this.#busy = false;
    this.failure(null);
    this.#paint();
  }

  /** The passkey line above the sign button (§C.11); `null` clears it. The button's label never changes. */
  line(text: string | null): void {
    this.#line = text ?? undefined;
    this.#paint();
  }

  /** Cancelled / timed out: the card exactly as it was, one line above the button. */
  failure(kind: SignFailure | null): void {
    this.#failure?.remove();
    this.#failure = undefined;
    if (kind === null) return;
    const [lead, rest] = kind === "cancelled" && this.#adds ? ADD_CANCELLED : FAILURE[kind];
    this.#failure = el("div", { class: "notice failure" }, [
      icon("i-alert"),
      el("div", {}, [el("b", {}, [lead]), rest]),
    ]);
    this.#bf.insertBefore(this.#failure, this.#why);
  }

  /**
   * The bottom of the card after the signature or at a stop; `null` puts the
   * card back to its own states (live · signed · …). The primary is the card's
   * one `.btn.sign`: a tap goes straight through, and an in-flight or asleep
   * button swallows it.
   */
  phase(end: CardEnd | null): void {
    // A control the new phase removes must not drop focus on the floor: it moves to
    // the new phase's first control, or to the card itself (a keyboard or a screen
    // reader lands on the result, not at the top of the page).
    const active = typeof document === "undefined" ? null : document.activeElement;
    const hadFocus = active instanceof HTMLElement && this.#bf.contains(active);
    this.#endSpec = end ?? undefined;
    this.#end.textContent = "";
    this.#endPrimary = undefined;
    this.#endWhy = undefined;
    if (end !== null) {
      // DCTA-143: a phase's sentence that names a flagged string (held.ts / stepup.ts, from notices.ts with the bank's
      // flags) sets its inert form apart as data, as the rows do.
      for (const n of end.notices) {
        this.#end.appendChild(
          el("div", { class: `notice${n.cls === undefined ? "" : ` ${n.cls}`}` }, [
            icon(n.icon),
            el("div", {}, [el("b", {}, refs(n.lead, this.#inert)), ...refs(n.rest ?? "", this.#inert)]),
          ]),
        );
      }
      if (end.extra !== undefined) this.#end.appendChild(end.extra);
      // The design's order: notices, the field, the line above the button, then the actions.
      const acts = el("div", { class: "acts" });
      if (end.primary !== undefined) {
        const p = end.primary;
        this.#endWhy = el("p", { class: "why", hidden: true });
        const btn = el("button", { class: "raw btn sign", type: "button", aria: { disabled: "true", busy: "false" } }, [
          icon(p.icon),
          // DCTA-232: no figure and no words after it (a protective card): the verb alone, never "Confirm · ".
          el("span", { class: "lbl" }, p.money === "" && p.rest === undefined ? [p.verb] : [`${p.verb} · `, ...(p.money === "" ? [] : [el("span", { class: "money" }, [p.money])]), ...(p.rest === undefined ? [] : [p.rest])]),
        ]);
        btn.addEventListener("click", () => {
          if (btn.getAttribute("aria-disabled") === "true" || btn.getAttribute("aria-busy") === "true") return;
          p.onSelect();
        });
        this.#endPrimary = btn;
        this.#end.appendChild(this.#endWhy);
        acts.appendChild(btn);
      }
      for (const a of end.acts) {
        const cls = a.kind === "btn" ? "raw btn" : `raw btn ${a.kind}`;
        if (a.href !== undefined) {
          acts.appendChild(el("a", { class: cls, href: a.href }, [a.label]));
          continue;
        }
        const b = el("button", { class: cls, type: "button" }, [a.label]);
        b.addEventListener("click", a.onSelect);
        acts.appendChild(b);
      }
      if (acts.childElementCount > 0) this.#end.appendChild(acts);
    }
    this.#paint();
    // §F.2 21h: the paused end is read first — focus on its lead, whatever had it (Safari never focuses a tapped button).
    const lead = end?.focus === "lead" ? this.#end.querySelector<HTMLElement>(".notice") : null;
    if (lead !== null) {
      lead.tabIndex = -1;
      lead.focus({ preventScroll: true });
      return;
    }
    if (hadFocus && end !== null) {
      const first = this.#end.querySelector<HTMLElement>("input, button, a[href]");
      if (first !== null) first.focus({ preventScroll: true });
      else {
        this.element.tabIndex = -1;
        this.element.focus({ preventScroll: true });
      }
    }
  }

  /** The primary's state inside a phase: asleep, busy (label kept), and the line above it. */
  primary(state: { readonly disabled: boolean; readonly busy: boolean; readonly why?: string | undefined }): void {
    this.#endPrimary?.setAttribute("aria-disabled", state.disabled || state.busy ? "true" : "false");
    this.#endPrimary?.setAttribute("aria-busy", state.busy ? "true" : "false");
    if (this.#endWhy !== undefined) {
      this.#endWhy.textContent = state.why ?? "";
      this.#endWhy.hidden = state.why === undefined;
    }
  }

  /**
   * The answers the signature is sent with (ask f): the gateway checks them against
   * the STORED interventions before it verifies the signature, so a card that let
   * Sign through without them would be refused — and a Yes never reaches here
   * (it pauses the card instead).
   */
  answers(): ScamAnswer[] {
    return [...this.#answers.values()].sort((a, b) =>
      a.step_id === b.step_id
        ? SCAM_QUESTION_IDS.indexOf(a.question_id) - SCAM_QUESTION_IDS.indexOf(b.question_id)
        : a.step_id.localeCompare(b.step_id),
    );
  }

  #allAnsweredNo(): boolean {
    const asked = (this.bundle.interventions ?? []).filter((i) => i.level === "question");
    return asked.every((i) => SCAM_QUESTION_IDS.every((q) => this.#answers.get(`${i.step_id}:${q}`)?.answer === "no"));
  }

  #signDisabled(): boolean {
    return this.#life !== "live" || this.#busy || this.#scamBlocked;
  }

  #scamNotice(intervention: Intervention, steps: readonly SignedStep[]): HTMLElement | undefined {
    const step = steps.find((s) => s.step_id === intervention.step_id);
    if (step === undefined) return undefined;
    // DCTA-136 (F10): a flagged name is said in its inert form here too, set apart as data.
    const r = describeStep(step, this.bundle.firewall?.data_flags ?? []);
    const notice: ScamNotice = composeScamNotice(intervention, {
      amount: r.amount,
      name: r.name,
      release: step.policy.hold_until === undefined ? undefined : dateTime(step.policy.hold_until),
      adHoc: step.type === "funds_transfer" && step.payee?.source === "paynow_lookup",
      // F43: "Earlier today" / "Yesterday" are measured from the bank's time of this card.
      now: this.bundle.signing_payload.created_at,
    });
    if (notice.lead === "" && notice.questions.length === 0) return undefined;
    const body: (Node | string)[] = [];
    if (notice.lead !== "") body.push(el("b", {}, refs(notice.lead, r.inert)));
    if (notice.check !== "") body.push(" ", ...refs(notice.check, r.inert));
    if (notice.after !== "") body.push(" ", ...refs(notice.after, r.inert));
    if (notice.questions.length > 0) {
      this.#scamBlocked = true;
      body.push(
        el(
          "div",
          { class: "qs" },
          notice.questions.map((q, i) => {
            const no = el("button", { class: "raw", type: "button", aria: { pressed: "false" } }, ["No"]);
            const yes = el("button", { class: "raw", type: "button", aria: { pressed: "false" } }, ["Yes"]);
            const qid = SCAM_QUESTION_IDS[i] ?? SCAM_QUESTION_IDS[0];
            scamQuestions += 1;
            const named = `scam-q-${String(scamQuestions)}`;
            const answer = (a: "no" | "yes"): void => {
              this.#answers.set(`${intervention.step_id}:${qid}`, { step_id: intervention.step_id, question_id: qid, answer: a });
              no.setAttribute("aria-pressed", a === "no" ? "true" : "false");
              yes.setAttribute("aria-pressed", a === "yes" ? "true" : "false");
              if (a === "yes") {
                this.#handlers.onScamYes?.(qid);
                return;
              }
              const was = this.#scamBlocked;
              this.#scamBlocked = !this.#allAnsweredNo();
              // §F.2 21e: the No that answers the last question wakes Sign; the polite line says so once. Focus stays here.
              this.#signNow = was && !this.#scamBlocked;
              this.#paint();
              // Claude's KNOWN_FIT ruling (8 Oct): on a short screen the questions come before Sign, so the card may scroll —
              // the No that wakes Sign brings it into view (smooth; at once under reduced motion), focus untouched.
              if (this.#signNow) this.#sign.scrollIntoView({ block: "nearest", behavior: reducedMotion(document.documentElement) ? "auto" : "smooth" });
              this.#signNow = false;
            };
            no.addEventListener("click", () => answer("no"));
            yes.addEventListener("click", () => answer("yes"));
            // §F.2 21d (F24): the question and its two answers are one group named by the question, so a screen reader
            // that lands on No hears the question first.
            return el("div", { role: "group", aria: { labelledby: named } }, [el("b", { id: named }, [q]), el("div", { class: "ans" }, [no, yes])]);
          }),
        ),
      );
    }
    // The hold level's wait is the pause: the clock, as 07 draws it; otherwise the shield.
    return el("div", { class: "notice scam" }, [icon(notice.after === "" ? "i-shield" : "i-clock"), el("div", {}, body)]);
  }

  /** The header's pill — its words (none: hidden) and its tone; the header is refitted only when the words change (P0-5). */
  #pill(text: string, pos: boolean): void {
    this.#status.classList.toggle("pos", pos);
    if (this.#status.textContent === text) return;
    this.#status.textContent = text;
    this.#status.hidden = text === "";
    fitHeader(this.#bh);
  }

  #paint(): void {
    const end = this.#endSpec;
    this.#end.hidden = end === undefined;
    // DCTA-209 (app.css 80): while a phase after the signature asks for the token (or a call), the block keeps its rule and
    // folds its dates and rows away — the card's row, the field and every action fit a 360 px phone. What was signed is
    // unchanged; the full block was on the card when it was signed and comes back when the card ends.
    this.#schedEl?.classList.toggle("compact", end !== undefined);
    if (end !== undefined) {
      this.#pill(end.status, end.pos === true);
      this.element.setAttribute("aria-label", end.label);
      for (const part of [this.#why, this.#sign, this.#endline, this.#edit, this.#talk, this.#show, ...this.#notices, ...this.#schedFoot]) part.hidden = true;
      this.element.classList.remove("ended", "void");
      this.#failure?.remove();
      this.#failure = undefined;
      this.#leaves.hidden = !end.leaves;
      return;
    }
    const life = this.#life;
    const n = this.bundle.signing_payload.steps.length;
    const ended = ENDS.has(life);
    // DCTA-209 (27m): a signature that created a schedule ends "Scheduled" — never "Sent", never "Signed · 1 of 1 done".
    const line = !ended ? undefined : life === "signed" && this.#mandate !== undefined ? scheduledEnd(this.#endAt, this.#mandate.first) : endLine(life as EndKind, this.#endAt, this.#done, this.#adds);
    const status =
      line !== undefined
        ? line.status
        : life === "editing"
          ? "Editing"
          : life === "superseded"
            ? this.#retired
              ? "Not sent"
              : "Superseded"
            : this.#revised
              ? "Revised"
              : "";
    // DCTA-143 (§F.2 18f): the positive pill is for a signature something of which went or waits — never over "Not sent".
    this.#pill(status, life === "signed" && line?.ok !== false);

    this.element.classList.toggle("editing", life === "editing");
    this.element.classList.toggle("superseded", life === "superseded");
    this.element.classList.toggle("expired", life === "expired");
    this.element.classList.toggle("ended", ended);
    // The bank refused to run it (POL-BAL-PROJECTED): a refusal, so its rule id may show (§6.8).
    this.element.classList.toggle("refused", life === "changed");
    this.element.classList.toggle("void", ended && life !== "signed");
    // What the card is, for a screen reader: "1 operation", or — on an add — "add a payee" (17's mock-up); DCTA-209: "repeat
    // payment" / "scheduled payment", and ", then the security token" when the payload asks for it (§0.2 Accessibility).
    const sched = this.#schedule;
    const what = sched !== undefined ? scheduleLead(sched).toLowerCase() : this.#adds ? "add a payee" : this.#count;
    const thenToken = sched !== undefined && this.bundle.signing_payload.steps[0]?.policy.outcome === "step_up_required" ? ", then the security token" : "";
    this.element.setAttribute(
      "aria-label",
      line !== undefined
        ? life === "signed"
          ? this.#mandate !== undefined
            ? `Bank: ${what}, scheduled`
            : line.ok
            ? `Bank: ${what}, signed`
            : `Bank: ${what}, signed — not sent`
          : `Bank: ${what}, ${line.status.toLowerCase()} — nothing to sign`
        : life === "editing"
          ? `Bank: ${what}, being edited — not signable`
          : life === "superseded"
            ? this.#retired
              ? "Bank: not sent"
              : "Bank: previous version, superseded"
            : this.#adds
              ? "Bank: add a payee, to sign"
              : sched !== undefined
                ? `Bank: ${what}, to sign${thenToken}`
                : `Bank: ${what} to sign${this.#revised ? " · revised" : ""}`,
    );

    // §C.10: an ended card has no sign button in any form — it leaves the card.
    if (ended) this.#sign.remove();
    const verb = n === 1 ? "Sign" : `Sign all ${String(n)}`;
    // DCTA-209 (27k): a schedule card's label names the rule: "Sign · S$200.00 a month", "Sign · every month".
    const label = sched === undefined || this.bundle.signing_payload.steps[0] === undefined ? undefined : signLabel(sched, this.bundle.signing_payload.steps[0]);
    this.#signLabel.replaceChildren(
      ...(label !== undefined
        ? [label.lead, ...(label.money === undefined ? [] : [el("span", { class: "money" }, [label.money])]), ...(label.rest === undefined ? [] : [label.rest])]
        : this.#adds
        ? ["Sign · add payee"] // §F.2 13e (§E.6)
        : unlockCard(this.bundle.signing_payload.steps)
          ? [UNLOCK_SIGN] // DCTA-232: the bank's unlock card
          : [verb, ...(this.#total === undefined ? [] : [" · ", el("span", { class: "money" }, [this.#total])])]),
    );
    this.#sign.hidden = ended || life === "superseded" || life === "editing";
    this.#sign.setAttribute("aria-busy", this.#busy ? "true" : "false");
    this.#sign.setAttribute("aria-disabled", this.#signDisabled() ? "true" : "false");
    // One line above the button: the scam questions' "Answer both to sign" first, else the passkey line. §F.2 21e: the
    // paint in which the last No wakes Sign also says "You can sign now." in this polite line — heard once, never shown.
    const why = life !== "live" ? undefined : this.#scamBlocked ? "Answer both to sign" : this.#line;
    const now = life === "live" && this.#signNow;
    this.#why.replaceChildren(...(now ? [el("span", { class: "vis-hidden" }, ["You can sign now."])] : []), ...(why === undefined ? [] : [now ? ` ${why}` : why]));
    this.#why.hidden = why === undefined && !now;
    this.#paintEnd(line);
    // DCTA-170: in the edit state in place, Done stands where Edit was (the footer below).
    const inPlace = life === "editing" && this.#editView !== undefined;
    this.#edit.hidden = ended || life === "superseded" || this.#scam || inPlace;
    this.#rows.hidden = inPlace;
    this.#erows.hidden = !inPlace;
    if (!inPlace) {
      this.#ehint.hidden = true;
      this.#eerr.hidden = this.#lineErr === undefined || life !== "live";
      this.element.dataset["busy"] = "false";
      this.element.removeAttribute("aria-busy");
    }
    this.#efoot.hidden = !inPlace || this.#editView?.last !== undefined;
    this.#elast.hidden = !inPlace || this.#editView?.last === undefined;
    this.#rmlines.hidden = inPlace || ended || life === "superseded" || this.#rmlines.childElementCount === 0;
    this.#talk.hidden = !(this.#scam && life === "live");
    this.#edit.textContent = this.#lead !== undefined || sched !== undefined ? "Not now" : life === "editing" ? "Keep it as it was" : "Edit";
    this.#show.hidden = life !== "superseded";
    this.#repl.hidden = !(life === "superseded" && this.#retired);
    // An add moves no money: no "leaves" line at all. DCTA-209 (27h): nor does a schedule at signing — its own line says so.
    this.#leaves.hidden = this.#adds || sched !== undefined || ended || life === "superseded" || life === "editing";
    // DCTA-170: the card's notices wait while it is edited in place — the bank's next card says them after Done.
    for (const notice of [...this.#schedFoot, ...this.#notices]) notice.hidden = ended || life === "superseded" || inPlace;
  }

  #paintEnd(line: ReturnType<typeof endLine> | undefined): void {
    this.#endline.hidden = line === undefined;
    if (line === undefined) return;
    this.#endline.classList.toggle("ok", line.ok);
    this.#endline.firstElementChild?.replaceWith(icon(line.icon));
    this.#endHead.textContent = line.headline;
    this.#endRest.textContent = line.consequence ?? "";
    this.#endRest.hidden = line.consequence === undefined;
    // DCTA-124 (Umakhan's ruling): a rule id is judge mode's — under a `.judge` root, never customer copy.
    if (line.rule === undefined || this.#handlers.judge?.() !== true) {
      this.#endRule?.remove();
      this.#endRule = undefined;
    } else if (this.#endRule === undefined) {
      this.#endRule = el("span", { class: "judge" }, [ruleId(line.rule, "small")]);
      this.#endText.appendChild(this.#endRule);
    }
    const action = line.action;
    const reachable =
      action !== undefined &&
      (this.#life === "expired" ||
        (this.#life === "signed" && (this.#mandate !== undefined ? this.#handlers.onView !== undefined : this.#handlers.onReceipt !== undefined)) ||
        (this.#life === "not-sent" && this.#handlers.onRetry !== undefined) ||
        (this.#life === "changed" && this.#handlers.onUpdate !== undefined && !this.#spent));
    this.#endGo.hidden = !reachable;
    // DCTA-124: an expired card a bank screen made has no words to ask again with — its way on builds a fresh card.
    const again = this.#lead !== undefined && this.#life === "expired";
    const label = this.#endGo.querySelector(".gl");
    if (label !== null) label.textContent = again ? "Check again" : (action?.label ?? "");
    const aria = again ? "Check and sign again" : action?.aria;
    if (aria !== undefined) this.#endGo.setAttribute("aria-label", aria);
    else this.#endGo.removeAttribute("aria-label");
  }
}

/** DCTA-232: the bank's unlock card — every step unlocks the money. */
function unlockCard(steps: readonly SignedStep[]): boolean {
  return protectiveOnly(steps) && steps.every((s) => s.type === "protective_action" && s.action === "unlock_funds");
}
